import { clsx } from "clsx";
import { type ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

interface StatCardProps {
  title: string;
  value?: string;
  valueClassName?: string;
  subtitle?: string;
  icon: ReactNode;
  iconBgClass?: string;
  trend?: { value: string; positive: boolean };
  viewAllHref?: string;
  linkLabel?: string;
  loading?: boolean;
  className?: string;
}

export function StatCard({
  title,
  value,
  valueClassName,
  subtitle,
  icon,
  iconBgClass = "bg-[#eff3ff] text-[#2447d9]",
  trend,
  viewAllHref,
  linkLabel,
  loading = false,
  className,
}: StatCardProps) {
  return (
    <div
      className={clsx(
        "bg-white rounded-xl border border-[#e2e8f0] p-5",
        "shadow-[0_1px_3px_0_rgb(0_0_0/0.07),0_1px_2px_-1px_rgb(0_0_0/0.07)]",
        "flex flex-col gap-4",
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#64748b]">{title}</p>

          {loading ? (
            <div className="mt-1 space-y-1.5">
              <div className="h-8 w-28 rounded-lg bg-[#f1f5f9] animate-pulse" />
              <div className="h-3 w-20 rounded bg-[#f1f5f9] animate-pulse" />
            </div>
          ) : (
            <>
              <p className={clsx("mt-1 text-2xl font-bold tracking-tight", valueClassName ?? "text-[#0f172a]")}>
                {value ?? "—"}
              </p>
              {subtitle && (
                <p className="mt-0.5 text-xs text-[#94a3b8]">{subtitle}</p>
              )}
              {trend && (
                <span
                  className={clsx(
                    "mt-1 inline-flex items-center gap-0.5 text-xs font-medium",
                    trend.positive ? "text-[#16a34a]" : "text-[#dc2626]",
                  )}
                >
                  {trend.positive ? "↑" : "↓"} {trend.value}
                </span>
              )}
            </>
          )}
        </div>
        <div
          className={clsx(
            "flex h-12 w-12 items-center justify-center rounded-full shrink-0 ml-3",
            iconBgClass,
          )}
        >
          {icon}
        </div>
      </div>

      {viewAllHref && (
        <Link
          to={viewAllHref}
          className="flex items-center gap-1 text-xs font-medium text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
        >
          {linkLabel ?? "View All"} <ArrowUpRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}
